// src/components/operationCenter/widgets/token/valuation/TwValuationRatiosWidget.tsx
// Displays the valuation ratios strip from ValuationTab (MC/TVL, MC/Fees, etc.).
import React from "react";
import type { WidgetProps, TokenWidgetConfig } from "@/types/dashboard";
import { TokenWidgetWrapper } from "../TokenWidgetWrapper";
import { TokenWidgetConfigPanel } from "../TokenWidgetConfig";
import { useTokenFundamentals } from "@/contexts/token";
import { KpiBox } from "@/components/tokenPage/shared/TokenDashboardComponents";

const formatRatio = (val: number): string => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "N/A";
  if (Math.abs(n) >= 10) return n.toFixed(1);
  if (Math.abs(n) >= 1) return n.toFixed(2);
  if (Math.abs(n) >= 0.1) return n.toFixed(3);
  return n.toFixed(4);
};

const formatCurrencySmart = (val: number): string => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "N/A";
  const abs = Math.abs(n);
  if (abs < 1) return `$${n.toFixed(4)}`;
  if (abs < 1000) return `$${n.toFixed(2)}`;
  if (abs < 1_000_000) return `$${(n / 1_000).toFixed(2)}k`;
  if (abs < 1_000_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  return `$${(n / 1_000_000_000).toFixed(2)}B`;
};

const ValuationRatiosInner: React.FC = () => {
  const { current, isLoading } = useTokenFundamentals();

  const ratioMcTvl = current.mcToTvl;
  const ratioMcFees = current.mcToFees;
  const ratioMcDau = current.mcToDau;
  const ratioMcTx = current.mcToTx;
  const ratioTvlToFees = current.tvlToFees;
  const ratioTvlToVol = current.tvlToVol;
  const ratioFeesToTxn = current.feesToTx;
  const ratioFeesToDau = current.feesToDau;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="loading loading-spinner loading-sm text-primary" />
      </div>
    );
  }

  return (
    <div className="p-3 grid grid-cols-2 gap-2">
      {ratioMcTvl != null && (
        <KpiBox label="MC / TVL" value={`${formatRatio(ratioMcTvl)}x`} />
      )}
      {ratioMcFees != null && (
        <KpiBox label="MC / Fees" value={`${formatRatio(ratioMcFees)}x`} />
      )}
      {ratioMcDau != null && (
        <KpiBox label="MC / DAU" value={formatCurrencySmart(ratioMcDau)} />
      )}
      {ratioMcTx != null && (
        <KpiBox label="MC / Tx" value={formatCurrencySmart(ratioMcTx)} />
      )}
      {ratioFeesToTxn != null && (
        <KpiBox label="Fees / Tx" value={formatCurrencySmart(ratioFeesToTxn)} />
      )}
      {ratioFeesToDau != null && (
        <KpiBox label="Fees / DAU" value={formatCurrencySmart(ratioFeesToDau)} />
      )}
      {ratioTvlToFees != null && (
        <KpiBox label="TVL / Fees" value={`${formatRatio(ratioTvlToFees)}x`} />
      )}
      {ratioTvlToVol != null && (
        <KpiBox label="TVL / Vol" value={`${formatRatio(ratioTvlToVol)}x`} />
      )}
    </div>
  );
};

const TwValuationRatiosWidget: React.FC<WidgetProps<TokenWidgetConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as TokenWidgetConfig;
  return (
    <TokenWidgetConfigPanel config={config} onConfigChange={onConfigChange} widgetType={widget.type}>
      <TokenWidgetWrapper ticker={config.ticker} providers={["fundamentals"]}>
        <ValuationRatiosInner />
      </TokenWidgetWrapper>
    </TokenWidgetConfigPanel>
  );
};

export default TwValuationRatiosWidget;
